import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiHome, FiBook, FiVideo, FiAward, FiUser, FiBarChart2, FiPlay, FiTrendingUp, FiCheckCircle
} from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { getLearnerMenu } from '../../components/layout/menuConfigs';
import EducationalParticles from '../../components/common/EducationalParticles';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import CourseCard from '../../components/common/CourseCard';
import { showToast } from '../../components/common/Toaster';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  learnerCourseAPI,
  learnerUtilityAPI,
} from '../../api/learnerEndpoints';
import { 
  DashboardContainer, 
  DashboardHeader, 
  StatsGrid, 
  StatCard, 
  StatIcon, 
  StatInfo, 
  SectionTitle,
  EmptyState,
  FilterBar,
  FilterSelect,
  SearchInput,
  ContentGrid,
} from '../dashboards/Dashboard.styles';

const MyLearningPage = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    search: '',
    grade_id: '',
    subject_id: '',
    status: '', // all, started, completed
  });

  const menuItems = getLearnerMenu();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // re-run stats when filters change if needed
  }, [filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [coursesRes, gradesRes, subjectsRes] = await Promise.all([
        learnerCourseAPI.getCourses({ per_page: 100 }),
        learnerUtilityAPI.getGrades(),
        learnerUtilityAPI.getSubjects(),
      ]);

      const coursesData = coursesRes.data.courses || [];
      setCourses(coursesData);
      setGrades(gradesRes.data.grades || []);
      setSubjects(subjectsRes.data.subjects || []);

      // Stats calculated from course data
      const startedCourses = coursesData.filter(c => c.started && !c.completed).length || 0;
      const completedCourses = coursesData.filter(c => c.completed).length || 0;
      const totalCourses = coursesData.length || 0;

      setStats({
        totalCourses,
        startedCourses,
        completedCourses,
        completionRate: totalCourses > 0 ? (completedCourses / totalCourses) * 100 : 0,
      });
    } catch (error) {
      console.error('Error fetching learning data:', error);
      showToast('error', 'Failed to load learning data');
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = () => {
    let filtered = courses;
    if (filters.search) {
      filtered = filtered.filter(course => 
        course.title.toLowerCase().includes(filters.search.toLowerCase()) ||
        course.description.toLowerCase().includes(filters.search.toLowerCase())
      );
    }
    if (filters.grade_id) {
      filtered = filtered.filter(course => course.grade_id === filters.grade_id);
    }
    if (filters.subject_id) {
      filtered = filtered.filter(course => course.subject_id === filters.subject_id);
    }
    if (filters.status === 'started') {
      filtered = filtered.filter(course => course.started && !course.completed);
    } else if (filters.status === 'completed') {
      filtered = filtered.filter(course => course.completed);
    }
    return filtered;
  };


  if (loading) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <DashboardContainer>
          <LoadingSpinner size="large" text="Loading your learning..." />
        </DashboardContainer>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={menuItems}>
      <EducationalParticles />
      <DashboardContainer>
        <DashboardHeader>
          <div>
            <h1>My Learning 📚</h1>
            <p>Manage your courses and track your progress in one place</p>
          </div>
          <Button 
            leftIcon={<FiVideo />} 
            onClick={() => navigate('/learner/browse')}
          >
            Browse More Courses
          </Button>
        </DashboardHeader>

        {/* Combined Statistics */}
        <StatsGrid>
          <StatCard>
            <StatIcon color="#2c5f34">
              <FiBook />
            </StatIcon>
            <StatInfo>
              <h3>{stats.totalCourses || 0}</h3>
              <p>Total Courses</p>
            </StatInfo>
          </StatCard>

          <StatCard>
            <StatIcon color="#10B981">
              <FiPlay />
            </StatIcon>
            <StatInfo>
              <h3>{stats.startedCourses || 0}</h3>
              <p>In Progress</p>
            </StatInfo>
          </StatCard>

          <StatCard>
            <StatIcon color="#F59E0B">
              <FiCheckCircle />
            </StatIcon>
            <StatInfo>
              <h3>{stats.completedCourses || 0}</h3>
              <p>Completed</p>
            </StatInfo>
          </StatCard>

          <StatCard>
            <StatIcon color="#8B5CF6">
              <FiTrendingUp />
            </StatIcon>
            <StatInfo>
              <h3>{stats.completionRate?.toFixed(1) || 0}%</h3>
              <p>Completion Rate</p>
            </StatInfo>
          </StatCard>
        </StatsGrid>

        {/* Filters */}
        <FilterBar>
          <SearchInput
            placeholder="Search your courses..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          <FilterSelect
            value={filters.grade_id}
            onChange={(e) => setFilters({ ...filters, grade_id: e.target.value })}
          >
            <option value="">All Grades</option>
            {grades.map((grade) => (
              <option key={grade.id} value={grade.id}>{grade.name}</option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={filters.subject_id}
            onChange={(e) => setFilters({ ...filters, subject_id: e.target.value })}
          >
            <option value="">All Subjects</option>
            {subjects.map((subject) => (
              <option key={subject.id} value={subject.id}>{subject.name}</option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All Courses</option>
            <option value="started">In Progress</option>
            <option value="completed">Completed</option>
          </FilterSelect>
        </FilterBar>

        {/* Courses List */}
        {filteredCourses().length === 0 ? (
          <EmptyState>
            <FiBook size={48} />
            <h3>No courses found</h3>
            <p>
              {courses.length === 0 
                ? "You haven't enrolled in any courses yet. Start browsing to find courses that interest you!"
                : "No courses match your current filters. Try adjusting your search criteria."}
            </p>
            <Button onClick={() => navigate('/learner/browse')}>
              Browse Courses
            </Button>
          </EmptyState>
        ) : (
          <ContentGrid>
            {filteredCourses().map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                showProgress={course.started && !course.completed}
                showRating={true}
                showInstructor={true}
                showGrade={true}
              />
            ))}
          </ContentGrid>
        )}

      </DashboardContainer>
    </DashboardLayout>
  );
};

export default MyLearningPage;


